import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { dealsAPI, DealActivityLog, DealActivityAction } from '../../services/api';
import {
  ClockIcon,
  ArrowPathIcon,
  FunnelIcon,
  DocumentTextIcon,
  TruckIcon,
  CurrencyDollarIcon,
  ExclamationCircleIcon,
  CheckCircleIcon,
  PlusCircleIcon,
  MinusCircleIcon,
  PencilSquareIcon,
} from '@heroicons/react/24/outline';
import { Card, CardBody, Badge, Skeleton } from '../ui';

const getActionIcon = (action: DealActivityAction) => {
  switch (action) {
    case 'CREATED': return <PlusCircleIcon className="h-5 w-5 text-success-500" />;
    case 'UPDATED': return <PencilSquareIcon className="h-5 w-5 text-primary-500" />;
    case 'PAYMENT_STATUS_CHANGED': return <CurrencyDollarIcon className="h-5 w-5 text-warning-500" />;
    case 'SHIPPING_STATUS_CHANGED': return <TruckIcon className="h-5 w-5 text-primary-500" />;
    case 'SHIPMENT_PROCESSED': return <TruckIcon className="h-5 w-5 text-success-500" />;
    case 'INVOICE_GENERATED': return <DocumentTextIcon className="h-5 w-5 text-success-500" />;
    case 'INVENTORY_DEDUCTED': return <MinusCircleIcon className="h-5 w-5 text-warning-500" />;
    case 'ISSUE_CREATED': return <ExclamationCircleIcon className="h-5 w-5 text-error-500" />;
    case 'ISSUE_RESOLVED': return <CheckCircleIcon className="h-5 w-5 text-success-500" />;
    case 'DOCUMENT_UPLOADED': return <DocumentTextIcon className="h-5 w-5 text-primary-500" />;
    default: return <ClockIcon className="h-5 w-5 text-neutral-500" />;
  }
};

const getActionBadgeVariant = (action: DealActivityAction): 'success' | 'warning' | 'error' | 'primary' | 'neutral' => {
  switch (action) {
    case 'CREATED': return 'success';
    case 'SHIPMENT_PROCESSED': return 'success';
    case 'INVOICE_GENERATED': return 'success';
    case 'ISSUE_RESOLVED': return 'success';
    case 'PAYMENT_STATUS_CHANGED': return 'warning';
    case 'INVENTORY_DEDUCTED': return 'warning';
    case 'ISSUE_CREATED': return 'error';
    default: return 'primary';
  }
};

const formatAction = (action: string): string => {
  return action.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const ActivityLogView: React.FC = () => {
  const location = useLocation();
  const [logs, setLogs] = useState<DealActivityLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [total, setTotal] = useState(0);
  const [filter, setFilter] = useState<string>('');
  const [page, setPage] = useState(0);
  const limit = 50;

  const isAdminContext = location.pathname.startsWith('/admin');
  const basePath = isAdminContext ? '/admin' : '/app';

  useEffect(() => {
    fetchLogs();
  }, [filter, page]);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const result = await dealsAPI.getAllActivityLogs({
        limit,
        offset: page * limit,
        action: filter || undefined,
      });
      setLogs(result.logs);
      setTotal(result.total);
    } catch (err) {
      setError('Failed to load activity logs');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const actionOptions: DealActivityAction[] = [
    'CREATED', 'UPDATED', 'STATUS_CHANGED', 'STAGE_CHANGED',
    'PAYMENT_STATUS_CHANGED', 'SHIPPING_STATUS_CHANGED',
    'SHIPMENT_PROCESSED', 'INVENTORY_DEDUCTED', 'ISSUE_CREATED', 'ISSUE_RESOLVED',
  ];

  if (loading && logs.length === 0) {
    return (
      <div className="space-y-4">
        {[1, 2, 3, 4, 5].map((i) => (
          <Skeleton key={i} className="h-20 w-full" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2">
          <FunnelIcon className="h-5 w-5 text-neutral-400" />
          <select
            value={filter}
            onChange={(e) => { setFilter(e.target.value); setPage(0); }}
            className="rounded-md border-neutral-300 text-sm focus:border-primary-500 focus:ring-primary-500"
          >
            <option value="">All Actions</option>
            {actionOptions.map((action) => (
              <option key={action} value={action}>{formatAction(action)}</option>
            ))}
          </select>
        </div>
        <button
          onClick={fetchLogs}
          className="p-2 text-neutral-500 hover:text-neutral-700 rounded-md hover:bg-neutral-100"
        >
          <ArrowPathIcon className="h-5 w-5" />
        </button>
        <span className="text-sm text-neutral-500">
          {total} total activities
        </span>
      </div>

      {error && (
        <div className="bg-error-50 text-error-700 p-4 rounded-lg">{error}</div>
      )}

      {/* Activity Timeline */}
      <div className="flow-root">
        <ul className="-mb-8">
          {logs.map((log, idx) => (
            <li key={log.id}>
              <div className="relative pb-8">
                {idx !== logs.length - 1 && (
                  <span className="absolute top-5 left-5 -ml-px h-full w-0.5 bg-neutral-200" />
                )}
                <div className="relative flex items-start space-x-3">
                  <div className="relative">
                    <div className="h-10 w-10 rounded-full bg-neutral-100 flex items-center justify-center ring-8 ring-white">
                      {getActionIcon(log.action)}
                    </div>
                  </div>
                  <div className="min-w-0 flex-1">
                    <Link to={`${basePath}/deals/${log.dealId}`} className="block">
                      <Card className="hover:shadow-md hover:border-primary-300 transition-all cursor-pointer">
                        <CardBody className="py-3 px-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <Badge variant={getActionBadgeVariant(log.action)} size="sm">
                                {formatAction(log.action)}
                              </Badge>
                              {log.deal && (
                                <span className="text-sm font-medium text-primary-600">
                                  {log.deal.orderNumber || `Order #${log.dealId}`}
                                </span>
                              )}
                            </div>
                            <span className="text-xs text-neutral-500">{formatDate(log.createdAt)}</span>
                          </div>
                          <p className="mt-1 text-sm text-neutral-700">{log.description}</p>
                          {log.user && (
                            <p className="mt-1 text-xs text-neutral-500">
                              by {log.user.firstName} {log.user.lastName}
                            </p>
                          )}
                        </CardBody>
                      </Card>
                    </Link>
                  </div>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>

      {/* Pagination */}
      {total > limit && (
        <div className="flex justify-center gap-2 mt-4">
          <button
            onClick={() => setPage(p => Math.max(0, p - 1))}
            disabled={page === 0}
            className="px-3 py-1 text-sm border rounded disabled:opacity-50"
          >
            Previous
          </button>
          <span className="px-3 py-1 text-sm">
            Page {page + 1} of {Math.ceil(total / limit)}
          </span>
          <button
            onClick={() => setPage(p => p + 1)}
            disabled={(page + 1) * limit >= total}
            className="px-3 py-1 text-sm border rounded disabled:opacity-50"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default ActivityLogView;
